import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track, trackOnce } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import {
  ArrowLeft, Camera, X, Save, Trash2, Image as ImageIcon, Building2, ZoomIn,
  Eye, Share2, Download, Phone, Mail, MapPin, FileText, Images, Plus,
} from "lucide-react";
import PhotoLightbox from "@/components/PhotoLightbox";

/** Helper: read a File into a base64 data URL with simple compression. */
async function fileToDataUrl(file, maxSide = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (Math.max(width, height) > maxSide) {
          const ratio = maxSide / Math.max(width, height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const c = document.createElement("canvas");
        c.width = width;
        c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * QuickReport — single-page editor and viewer for a pikaraportti.
 * Routes:
 *  - /app/pikaraportti/uusi    → create
 *  - /app/pikaraportti/:id     → view + edit
 */
export default function QuickReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const fileRef = useRef();
  const cameraRef = useRef();
  const isNew = !id || id === "uusi";

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({
    title: "",
    text_body: "",
    customer_name: "",
    property_id: null,
    photos: [],
    contact: { name: "", phone: "", email: "", address: "", notes: "" },
    findings_text: "",
    findings_show_to_customer: true,
    findings_notify_office: false,
    materials: [],
    checklist: [],
  });
  const [shareToken, setShareToken] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data: doc } = await api.get(`/quick-reports/${id}`);
        setData({
          title: doc.title || "",
          text_body: doc.text_body || "",
          customer_name: doc.customer_name || "",
          property_id: doc.property_id || null,
          photos: doc.photos || [],
          contact: doc.contact || { name: "", phone: "", email: "", address: "", notes: "" },
          findings_text: doc.findings_text || "",
          findings_show_to_customer: doc.findings_show_to_customer !== false,
          findings_notify_office: !!doc.findings_notify_office,
          materials: doc.materials || [],
          checklist: doc.checklist || [],
        });
        if (doc.share_token) setShareToken(doc.share_token);
      } catch (e) {
        toast.error(t("qr.notFound"));
        navigate("/app");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const onPickPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 12);
    const newPhotos = [];
    for (const f of arr) {
      try {
        const dataUrl = await fileToDataUrl(f);
        newPhotos.push({ id: Math.random().toString(36).slice(2, 12), data: dataUrl, caption: "" });
      } catch (e) {
        toast.error(`Kuva ${f.name} epäonnistui`);
      }
    }
    setData((d) => ({ ...d, photos: [...(d.photos || []), ...newPhotos] }));
  };

  const removePhoto = (idx) => {
    setData((d) => ({ ...d, photos: d.photos.filter((_, i) => i !== idx) }));
  };
  const setCaption = (idx, caption) => {
    setData((d) => ({ ...d, photos: d.photos.map((p, i) => (i === idx ? { ...p, caption } : p)) }));
  };

  const save = async () => {
    if (!data.title.trim()) { toast.error(t("qr.titleRequired")); return; }
    setBusy(true);
    try {
      const payload = {
        title: data.title.trim(),
        text_body: data.text_body.trim(),
        customer_name: data.customer_name.trim(),
        property_id: data.property_id || null,
        photos: data.photos.map((p) => ({ data: p.data, caption: p.caption })),
        contact: {
          name: (data.contact?.name || "").trim() || null,
          phone: (data.contact?.phone || "").trim() || null,
          email: (data.contact?.email || "").trim() || null,
          address: (data.contact?.address || "").trim() || null,
          notes: (data.contact?.notes || "").trim() || null,
        },
        findings_text: (data.findings_text || "").trim(),
        findings_show_to_customer: !!data.findings_show_to_customer,
        findings_notify_office: !!data.findings_notify_office,
        materials: (data.materials || []).filter((m) => (m.name || "").trim()),
        checklist: (data.checklist || []).filter((c) => (c.label || "").trim()),
      };
      if (isNew) {
        const { data: created } = await api.post("/quick-reports", payload);
        // Activation analytics — fire every time on creation, plus a
        // one-shot "First Report Created" event the user's first one.
        track("Report Created", { type: "quick" });
        if (user?.user_id) {
          trackOnce("First Report Created", user.user_id, { type: "quick" });
        }
        toast.success(t("qr.savedToast"));
        navigate(`/app/pikaraportti/${created.quick_id}`, { replace: true });
      } else {
        const { data: updated } = await api.patch(`/quick-reports/${id}`, payload);
        if (updated?.share_token) setShareToken(updated.share_token);
        toast.success(t("qr.savedToastShort"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("qr.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(t("qr.deleteConfirm"))) return;
    try {
      await api.delete(`/quick-reports/${id}`);
      toast.success(t("qr.deleted"));
      navigate("/app");
    } catch (_e) {
      toast.error(t("qr.deleteFailed"));
    }
  };

  // Dynamic browser/PDF document title — drives default PDF filename
  // when user prints from this editor view (Cmd+P / Tulosta).
  useEffect(() => {
    if (isNew) return;
    const label = (data.title || "").trim();
    const prev = document.title;
    document.title = label ? `${t("qr.eyebrow")} - ${label}` : t("qr.eyebrow");
    return () => { document.title = prev; };
  }, [data.title, isNew, t]);

  if (loading) return <div className="p-8 text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto" data-testid="quick-report-editor">
      <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-4" data-testid="quick-back">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("qr.back")}
      </button>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">{t("qr.eyebrow")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">
            {isNew ? t("qr.titleNew") : data.title || t("qr.titleDefault")}
          </h1>
          <p className="mt-2 text-slate-600">
            {t("qr.subtitle")}
          </p>
        </div>
        {!isNew && (
          <div className="flex items-center gap-2 flex-wrap">
            {shareToken && (
              <>
                <button
                  type="button"
                  onClick={() => window.open(`/q/${shareToken}`, "_blank", "noopener")}
                  className="btn-ghost text-sm"
                  data-testid="quick-preview"
                >
                  <Eye className="h-4 w-4" /> {t("qr.preview")}
                </button>
                <button
                  type="button"
                  onClick={() => window.open(`/q/${shareToken}?print=1`, "_blank")}
                  className="btn-ghost text-sm"
                  data-testid="quick-pdf"
                >
                  <Download className="h-4 w-4" /> {t("qr.downloadPdf")}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const url = `${window.location.origin}/q/${shareToken}`;
                    try {
                      if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(url);
                      } else {
                        const ta = document.createElement("textarea");
                        ta.value = url;
                        ta.style.position = "fixed";
                        ta.style.opacity = "0";
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                      }
                      toast.success(t("qr.linkCopied"));
                    } catch {
                      toast.message(t("qr.copyManual"), { description: url });
                    }
                  }}
                  className="btn-ghost text-sm"
                  data-testid="quick-share"
                >
                  <Share2 className="h-4 w-4" /> {t("qr.shareLink")}
                </button>
              </>
            )}
            <button onClick={remove} className="btn-ghost text-sm text-rose-600" data-testid="quick-delete">
              <Trash2 className="h-4 w-4" /> {t("qr.delete")}
            </button>
          </div>
        )}
      </div>

      <div className="card-soft p-5 md:p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("qr.workName")} *</label>
          <input
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            placeholder={t("qr.workNamePh")}
            className="input-base"
            data-testid="quick-title"
          />
        </div>

        {/* Lisätiedot — siirretty ylimmäiseksi (ennen kuvia) jotta käyttäjän alkuperäinen kirjoitus ei jää kuvien alle */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("qr.notes")}</label>
          <textarea
            value={data.text_body}
            onChange={(e) => setData({ ...data, text_body: e.target.value })}
            rows={4}
            className="input-base resize-y"
            data-testid="quick-text"
          />
        </div>

        {/* Havainnot / jatkotoimenpiteet — yksi kenttä + kaksi valintaa.
            Pidetään ylimpänä jotta kentät eivät jää kuvien alle mobiilissa. */}
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="quick-findings-block">
          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-1">
              {t("qr.findings")}
              <span className="text-slate-500 font-normal text-xs"> {t("qr.findingsOptional")}</span>
            </label>
            <textarea
              value={data.findings_text}
              onChange={(e) => setData({ ...data, findings_text: e.target.value })}
              placeholder={t("qr.findingsPh")}
              rows={3}
              className="input-base resize-y bg-white"
              data-testid="quick-findings-text"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-400 transition-colors">
              <input
                type="checkbox"
                checked={!!data.findings_show_to_customer}
                onChange={(e) => setData({ ...data, findings_show_to_customer: e.target.checked })}
                className="h-4 w-4 rounded text-emerald-500 focus:ring-emerald-500"
                data-testid="quick-findings-show"
              />
              <span className="text-sm font-medium text-slate-700">{t("qr.showToCustomer")}</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-400 transition-colors">
              <input
                type="checkbox"
                checked={!!data.findings_notify_office}
                onChange={(e) => setData({ ...data, findings_notify_office: e.target.checked })}
                className="h-4 w-4 rounded text-amber-500 focus:ring-amber-500"
                data-testid="quick-findings-notify"
              />
              <span className="text-sm font-medium text-slate-700">{t("qr.notifyOffice")}</span>
            </label>
          </div>
          {!data.findings_show_to_customer && data.findings_text && (
            <p className="text-xs text-slate-500">{t("qr.hiddenHint")}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {t("qr.customer")} <span className="text-slate-400 font-normal">{t("common.optional")}</span>
          </label>
          <input
            value={data.customer_name}
            onChange={(e) => setData({ ...data, customer_name: e.target.value })}
            placeholder={t("qr.customerPh")}
            className="input-base"
            data-testid="quick-customer"
          />
        </div>

        {/* Tarkemmat yhteystiedot — yksityisasiakkaille (omakotitalot, remontit) */}
        <details className="group rounded-2xl bg-slate-50/60 border border-slate-200 px-4 py-3" data-testid="quick-contact-details">
          <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-slate-900">
            <span className="inline-flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-slate-400" />
              {t("qr.contactDetails")}
              <span className="text-slate-400 font-normal text-xs">{t("common.optional")}</span>
            </span>
            <span className="text-xs text-slate-400 group-open:rotate-180 transition-transform">▾</span>
          </summary>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{t("qr.contactName")}</label>
              <input
                value={data.contact?.name || ""}
                onChange={(e) => setData({ ...data, contact: { ...data.contact, name: e.target.value } })}
                placeholder={t("qr.contactNamePh")}
                className="input-base"
                data-testid="quick-contact-name"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{t("qr.phone")}</label>
              <input
                value={data.contact?.phone || ""}
                onChange={(e) => setData({ ...data, contact: { ...data.contact, phone: e.target.value } })}
                placeholder="040 123 4567"
                inputMode="tel"
                className="input-base"
                data-testid="quick-contact-phone"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{t("qr.email")}</label>
              <input
                value={data.contact?.email || ""}
                onChange={(e) => setData({ ...data, contact: { ...data.contact, email: e.target.value } })}
                placeholder="nimi@yritys.fi"
                inputMode="email"
                className="input-base"
                data-testid="quick-contact-email"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{t("qr.address")}</label>
              <input
                value={data.contact?.address || ""}
                onChange={(e) => setData({ ...data, contact: { ...data.contact, address: e.target.value } })}
                placeholder="Esimerkkitie 1, 00100 Helsinki"
                className="input-base"
                data-testid="quick-contact-address"
              />
            </div>
          </div>
        </details>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("qr.photos")}</label>
          {data.photos.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              {data.photos.map((p, i) => (
                <div key={p.id || i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm" data-testid={`quick-photo-${i}`}>
                  <button
                    type="button"
                    onClick={() => setLightbox({ photos: data.photos, index: i })}
                    className="block w-full aspect-[4/3] bg-slate-100 group relative"
                  >
                    <img src={p.data} alt={p.caption || "Kuva"} className="w-full h-full object-cover" draggable={false} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <ZoomIn className="h-6 w-6 text-white" />
                    </div>
                  </button>
                  <div className="p-3">
                    <textarea
                      value={p.caption || ""}
                      onChange={(e) => setCaption(i, e.target.value)}
                      placeholder={t("qr.captionPh")}
                      rows={2}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 resize-y bg-white"
                      data-testid={`quick-photo-caption-${i}`}
                    />
                    <div className="flex justify-end mt-1">
                      <button onClick={() => removePhoto(i)} className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1" data-testid={`quick-photo-remove-${i}`}>
                        <X className="h-3 w-3" /> {t("qr.removePhoto")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => onPickPhotos(e.target.files)}
            data-testid="quick-photo-input"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPickPhotos(e.target.files)}
            data-testid="quick-camera-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="btn-ghost text-sm border-2 border-dashed border-slate-300 py-4 hover:border-emerald-400 hover:bg-emerald-50/30"
              data-testid="quick-camera-add"
            >
              <Camera className="h-4 w-4" /> {t("qr.takePhoto")}
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="btn-ghost text-sm border-2 border-dashed border-slate-300 py-4 hover:border-emerald-400 hover:bg-emerald-50/30"
              data-testid="quick-gallery-add"
            >
              <Images className="h-4 w-4" /> {t("qr.fromGallery")}
            </button>
          </div>
        </div>

        {/* Käytetyt tarvikkeet */}
        <QuickMaterialsBlock
          materials={data.materials}
          onChange={(materials) => setData({ ...data, materials })}
        />
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Link to="/app" className="btn-ghost text-sm">{t("qr.cancel")}</Link>
        <button onClick={save} disabled={busy || !data.title.trim()} className="btn-primary disabled:opacity-50" data-testid="quick-save">
          <Save className="h-4 w-4" /> {busy ? t("qr.saving") : isNew ? t("qr.create") : t("qr.save")}
        </button>
      </div>

      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* -------- Helpers: lightweight materials editor — same UX as long report's MaterialsUsedBlock -------- */

function QuickMaterialsBlock({ materials, onChange }) {
  const { t } = useT();
  const list = Array.isArray(materials) ? materials : [];

  // Bottom "add" row inputs
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unitLabel, setUnitLabel] = useState("kpl");

  // Lazy-load saved company materials for the pills
  const [savedMaterials, setSavedMaterials] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded) return;
    let cancel = false;
    api.get("/company/materials").then(({ data }) => {
      if (cancel) return;
      setSavedMaterials(data?.materials || data || []);
    }).catch(() => {}).finally(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, [loaded]);

  const addItem = (payload) => {
    const n = (payload?.name ?? name).trim();
    if (!n) return;
    const q = payload?.qty ?? (Number(qty) || 1);
    const u = ((payload?.unit ?? unitLabel) || "kpl").toString().trim() || "kpl";
    const existing = list.findIndex((m) => (m.name || "").toLowerCase() === n.toLowerCase());
    if (existing >= 0) {
      const merged = [...list];
      const currentQty = Number(merged[existing].qty) || 0;
      merged[existing] = { ...merged[existing], qty: currentQty + Number(q), unit: u };
      onChange(merged);
    } else {
      onChange([...list, { name: n, qty: Number(q), unit: u }]);
    }
    setName(""); setQty(""); setUnitLabel("kpl");
  };

  const updateRow = (i, patch) => {
    const next = list.map((m, idx) => (idx === i ? { ...m, ...patch } : m));
    onChange(next);
  };
  const removeRow = (i) => onChange(list.filter((_, idx) => idx !== i));

  const picks = savedMaterials.map((m) => ({ name: (m.name || m || "").toString(), unit: m.unit || "kpl" })).filter((p) => p.name);

  return (
    <div className="rounded-2xl bg-slate-50/60 border border-slate-200 p-4" data-testid="quick-materials-block">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-slate-700">{t("qr.materials")}</label>
        <span className="text-xs text-slate-400">{list.length} {list.length === 1 ? t("qr.row") : t("qr.rows")}</span>
      </div>

      {picks.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {picks.map((p, i) => (
            <button
              key={`${p.name}-${i}`}
              type="button"
              onClick={() => addItem({ name: p.name, qty: 1, unit: p.unit })}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-white border border-blue-200 text-blue-800 hover:bg-blue-50"
              data-testid={`quick-material-pick-${i}`}
              title={`Lisää 1 ${p.unit || "kpl"}`}
            >
              + {p.name} <span className="text-blue-400 font-normal">({p.unit || "kpl"})</span>
            </button>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {list.map((m, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200" data-testid={`quick-material-row-${i}`}>
              <span className="flex-1 text-sm text-slate-800 truncate">{m.name}</span>
              <input
                type="number" min="0" step="0.1"
                value={m.qty}
                onChange={(e) => updateRow(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })}
                className="w-16 px-2 py-1 rounded border border-slate-200 text-sm text-right bg-white"
                data-testid={`quick-material-qty-${i}`}
              />
              <input
                value={m.unit || "kpl"}
                onChange={(e) => updateRow(i, { unit: e.target.value })}
                className="w-14 px-2 py-1 rounded border border-slate-200 text-sm bg-white"
                data-testid={`quick-material-unit-${i}`}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="p-1 rounded text-slate-400 hover:text-red-600"
                data-testid={`quick-material-remove-${i}`}
                aria-label="Poista tarvike"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Tarvike"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          data-testid="quick-material-name"
        />
        <div className="flex gap-1.5">
          <input
            type="number" min="0" step="0.1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="Määrä"
            inputMode="decimal"
            className="w-20 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm text-right"
            data-testid="quick-material-newqty"
          />
          <input
            value={unitLabel}
            onChange={(e) => setUnitLabel(e.target.value)}
            placeholder="kpl"
            className="w-16 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            data-testid="quick-material-newunit"
          />
          <button
            type="button"
            onClick={() => addItem()}
            disabled={!name.trim()}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 hover:bg-emerald-600"
            data-testid="quick-material-add"
          >
            <Plus className="h-4 w-4" /> {t("common.add")}
          </button>
        </div>
      </div>
    </div>
  );
}
